import { inject, Injectable } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { FieldSortConfig, getActiveBinding } from '@blaze-shared/elements-domain';
import {
  DatasetRowGetOptions,
  DatasetRowsService,
  SchemaService,
} from '@blaze-shared/schema-data-access';
import { removeNonSelectedColumns } from '@blaze-shared/schema-domain';
import { isDeepEqual, NONE_OPTION_VALUE } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { ElementDefinitionsService } from '../../../../../services/element-definitions.service';
import { ElementStoreViewService } from '../../../../../services/element-store-view.service';
import { hasDataProps } from '../../../../element-model/element-props-guards';
import { isRadioOrCheckboxElement } from '../../../../element-model/element-type-guards';
import { DataProviderQuery, InputDataPackage } from '../../../io-data.models';
import { DataSourceProvider } from '../data-source.data-provider';
import { SortResultsService } from '../utils/sort-results.service';

import { getDatasetRowGetConfig } from './get-dataset-row-get-config';

@Injectable({ providedIn: 'root' })
export class DatasetDataProvider implements DataSourceProvider {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly schemaService = inject(SchemaService);
  private readonly sortResultsService = inject(SortResultsService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  readonly sourceType = 'dataset' as const;

  getDataStream({ source, element, binding }: DataProviderQuery): Observable<InputDataPackage> {
    const datasetId = source.id;
    // Fetch sort config
    let sortConfig$: Observable<FieldSortConfig | FieldSortConfig[] | undefined> = of(undefined);
    const optionsSortSupport =
      this.elementDefinitionsService.getFeature(element.type, 'optionsSortSupport') === true;
    if (optionsSortSupport) {
      sortConfig$ = this.elementStoreView.getElement(element.id).pipe(
        map((el) => {
          if (!hasDataProps(el) || !getActiveBinding(el.props.data, 'optionsList')) {
            return undefined;
          }
          const sortConfig = getValueByPath<FieldSortConfig | FieldSortConfig[]>(
            el.props,
            'content.sort',
          );
          if (Array.isArray(sortConfig)) {
            return sortConfig.filter(({ field }) => !!field && field !== NONE_OPTION_VALUE);
          }
          if (sortConfig?.field && sortConfig.field !== NONE_OPTION_VALUE) {
            return sortConfig;
          }
          return undefined;
        }),
        distinctUntilChanged(isDeepEqual),
      );
    }
    // Reactively observe custom field columns so editor changes re-trigger the fetch
    let customFieldsProjectedColumns$: Observable<string[]> = of([]);
    if (isRadioOrCheckboxElement(element)) {
      customFieldsProjectedColumns$ = this.elementStoreView.getElement(element.id).pipe(
        map((el) => {
          if (!el || !isRadioOrCheckboxElement(el)) {
            return [];
          }
          const customFields = el.props.content.customFields;
          if (!customFields) {
            return [];
          }
          return customFields.map((f) => f.column).filter((s): s is string => !!s);
        }),
        distinctUntilChanged(isDeepEqual),
      );
    }
    // Fetch data and schema
    const result$ = combineLatest({
      sortConfig: sortConfig$,
      customFieldsProjectedColumns: customFieldsProjectedColumns$,
      datasetChanges: this.datasetRowsService.observeDatasetAndRelatedChanged(datasetId).pipe(
        // Emit undefined to trigger at startup
        startWith(undefined),
      ),
    }).pipe(
      switchMap(({ sortConfig, customFieldsProjectedColumns }) => {
        const config: DatasetRowGetOptions = getDatasetRowGetConfig(
          datasetId,
          binding,
          sortConfig,
          customFieldsProjectedColumns,
        );
        return this.datasetRowsService.get(config).pipe(
          // Append the schema - for now obtainSchema is used as it's solid
          switchMap((data) => {
            return this.schemaService.obtainSchema(datasetId).pipe(
              map((schema) => ({
                data,
                schema,
                sortConfig,
                projectColumns: config.projectColumns,
              })),
            );
          }),
        );
      }),
    );

    // Combine data with sorting method
    return result$.pipe(
      map(({ data, schema, sortConfig, projectColumns }) => {
        const sortedData = this.sortResultsService.sortIfApplicable(
          element?.type,
          binding,
          data,
          sortConfig,
        );

        // When projectColumns is enabled, rows only contain the bound columns (+ _id).
        // Trim the schema to match the projected data shape.
        const trimmedSchema =
          projectColumns?.length && schema
            ? { ...schema, columns: removeNonSelectedColumns(schema.columns, projectColumns) }
            : schema;

        return {
          sourceType: 'dataset' as const,
          data: sortedData,
          schema: trimmedSchema,
        };
      }),
    );
  }
}
